import { create } from "zustand";
import { registerScreenRecordingSession } from "./screen-recording-store";

import type { ScreenshotScreenMeta } from "@/lib/pointing-transform";
import type { ImageAttachment } from "@/components/panels/image-preview-strip";
import {
  appendInkPoint,
  storedInkPoints,
  streamlineInkPoint,
} from "@/lib/sketch-stroke/user-ink-stroke";
import { subscribeDesktopChatAuthScope } from "./desktop-chat-auth-scope";
import type { TextAttentionAnnotation } from "./text-attention-annotations";

const MAX_ANNOTATIONS = 20;

export type TextAttentionCapture = {
  chapterId: string;
  displayId: number;
  revision: number;
  status: "capturing" | "ready" | "error";
  attachment?: ImageAttachment;
  screen?: ScreenshotScreenMeta;
};

export type TextAttentionState = {
  scopeKey: string | null;
  recordingId: string | null;
  annotations: TextAttentionAnnotation[];
  activeId: string | null;
  revisions: Record<string, number>;
  nativeRevisions: Record<string, number>;
  revisionOffsets: Record<string, number>;
  foreignScrolls: Record<string, string>;
  changing: Record<string, boolean>;
  captures: Record<string, TextAttentionCapture>;
};

const initial: TextAttentionState = {
  scopeKey: null,
  recordingId: null,
  annotations: [],
  activeId: null,
  revisions: {},
  nativeRevisions: {},
  revisionOffsets: {},
  foreignScrolls: {},
  changing: {},
  captures: {},
};

export const useTextAttentionStore = create<TextAttentionState>(() => initial);

export function textChapterId(
  recordingId: string,
  displayId: number,
  revision: number,
): string {
  return `${recordingId}/${displayId}:${revision}`;
}

export function beginTextAttentionSession(recordingId: string): void {
  registerScreenRecordingSession(recordingId, "text");
  if (useTextAttentionStore.getState().recordingId === recordingId) return;
  useTextAttentionStore.setState({
    recordingId,
    activeId: null,
    revisions: {},
    nativeRevisions: {},
    revisionOffsets: {},
    foreignScrolls: {},
    changing: {},
  });
}

export function endTextAttentionSession(recordingId: string): void {
  if (useTextAttentionStore.getState().recordingId !== recordingId) return;
  useTextAttentionStore.setState({ recordingId: null, activeId: null });
}

export function clearTextAttentionDraft(): void {
  useTextAttentionStore.setState((state) => ({
    ...initial,
    scopeKey: state.scopeKey,
  }));
}

export function setTextAttentionScope(scopeKey: string): void {
  if (useTextAttentionStore.getState().scopeKey === scopeKey) return;
  useTextAttentionStore.setState({ ...initial, scopeKey });
}

subscribeDesktopChatAuthScope(() => {
  useTextAttentionStore.setState(initial);
});

export function ingestTextAttentionViewChange(event: {
  recordingId: string;
  displayId: number | null;
  revision: number;
  phase: "start" | "settled";
  kind: "scroll" | "click";
}): void {
  const state = useTextAttentionStore.getState();
  if (
    state.recordingId !== event.recordingId ||
    event.displayId === null ||
    event.kind !== "scroll" ||
    !Number.isInteger(event.revision)
  )
    return;
  const key = String(event.displayId);
  if (event.revision < (state.nativeRevisions[key] ?? 0)) return;
  const revision = event.revision + (state.revisionOffsets[key] ?? 0);
  useTextAttentionStore.setState({
    revisions: { ...state.revisions, [key]: revision },
    nativeRevisions: { ...state.nativeRevisions, [key]: event.revision },
    changing: { ...state.changing, [key]: event.phase === "start" },
    activeId: event.phase === "start" ? null : state.activeId,
  });
}

/** A voice turn can scroll while text capture is paused by native priority. */
export function ingestVoiceScrollDuringText(event: {
  recordingId: string;
  displayId: number | null;
  revision: number;
  phase: "start" | "settled";
  kind: "scroll" | "click";
}): void {
  const state = useTextAttentionStore.getState();
  if (!state.recordingId || event.displayId === null || event.kind !== "scroll")
    return;
  const key = String(event.displayId);
  const scrollId = `${event.recordingId}:${event.revision}`;
  if (event.phase === "start") {
    if (state.foreignScrolls[key] === scrollId) return;
    useTextAttentionStore.setState({
      revisions: { ...state.revisions, [key]: (state.revisions[key] ?? 0) + 1 },
      revisionOffsets: {
        ...state.revisionOffsets,
        [key]: (state.revisionOffsets[key] ?? 0) + 1,
      },
      foreignScrolls: { ...state.foreignScrolls, [key]: scrollId },
      changing: { ...state.changing, [key]: true },
      activeId: null,
    });
  } else if (state.foreignScrolls[key] === scrollId) {
    useTextAttentionStore.setState({
      changing: { ...state.changing, [key]: false },
    });
  }
}

/** Stored points are streamlined at ingestion and carry their final pen
 *  pressure; the prompt reads the same streamlined position, within a few
 *  points of the raw pointer. Stored points never change. */
export function ingestTextAttentionGesture(event: {
  recordingId: string;
  strokeId: string;
  phase: "start" | "move" | "end";
  tMs?: number;
  displayId: number | null;
  x: number;
  y: number;
}): string | null {
  const state = useTextAttentionStore.getState();
  if (
    state.recordingId !== event.recordingId ||
    event.displayId === null ||
    state.changing[String(event.displayId)] ||
    !Number.isFinite(event.x) ||
    !Number.isFinite(event.y)
  )
    return null;
  const revision = state.revisions[String(event.displayId)] ?? 0;
  const active = state.annotations.find((item) => item.id === state.activeId);
  const sameView =
    active?.displayId === event.displayId && active.revision === revision;
  const chapter = textChapterId(event.recordingId, event.displayId, revision);
  const raw = { x: event.x, y: event.y, tMs: event.tMs };
  let annotations = state.annotations;
  let activeId = state.activeId;
  if (event.phase === "start") {
    if (!sameView) {
      if (annotations.length >= MAX_ANNOTATIONS) return null;
      activeId = `${event.recordingId}/${event.strokeId}`;
      annotations = [
        ...annotations,
        {
          id: activeId,
          number: Math.max(0, ...annotations.map((item) => item.number)) + 1,
          displayId: event.displayId,
          revision,
          anchor: { x: event.x, y: event.y },
          strokes: [],
          comment: "",
        },
      ];
    }
    annotations = annotations.map((item) =>
      item.id === activeId
        ? {
            ...item,
            strokes: [...item.strokes, [streamlineInkPoint(null, raw)!]],
          }
        : item,
    );
  } else if (active && sameView && active.strokes.length > 0) {
    const strokes = [...active.strokes];
    const last = strokes[strokes.length - 1]!;
    // A move under 1 pt stores nothing; a release also stores where it lifted.
    const points = storedInkPoints(
      last[last.length - 1] ?? null,
      raw,
      event.phase === "end",
    );
    if (points.length === 0) return chapter;
    strokes[strokes.length - 1] = points.reduce(
      (stroke, point) => appendInkPoint(stroke, point),
      last,
    );
    annotations = annotations.map((item) =>
      item.id === activeId ? { ...item, strokes } : item,
    );
  } else {
    return null;
  }
  useTextAttentionStore.setState({ annotations, activeId });
  return chapter;
}

export function finishTextAttentionHold(recordingId: string): void {
  const state = useTextAttentionStore.getState();
  if (state.recordingId !== recordingId || !state.activeId) return;
  useTextAttentionStore.setState({ activeId: null });
}

export function setTextAttentionComment(id: string, comment: string): void {
  useTextAttentionStore.setState((state) => ({
    annotations: state.annotations.map((item) =>
      item.id === id ? { ...item, comment } : item,
    ),
  }));
}

export function setTextAttentionCapture(capture: TextAttentionCapture): void {
  useTextAttentionStore.setState((state) => ({
    captures: { ...state.captures, [capture.chapterId]: capture },
  }));
}

export function removeTextAttentionCapture(attachmentId: string): void {
  useTextAttentionStore.setState((state) => {
    const captures = Object.fromEntries(
      Object.entries(state.captures).filter(
        ([, capture]) => capture.attachment?.id !== attachmentId,
      ),
    );
    const retainedChapters = new Set(Object.keys(captures));
    return {
      captures,
      annotations: state.annotations.filter((item) =>
        retainedChapters.has(
          textChapterId(item.id.split("/")[0]!, item.displayId, item.revision),
        ),
      ),
    };
  });
}

export function removeTextAttentionChapter(chapterId: string): void {
  useTextAttentionStore.setState((state) => {
    const captures = { ...state.captures };
    delete captures[chapterId];
    const annotations = state.annotations.filter(
      (item) =>
        textChapterId(item.id.split("/")[0]!, item.displayId, item.revision) !==
        chapterId,
    );
    return {
      captures,
      annotations,
      activeId: annotations.some((item) => item.id === state.activeId)
        ? state.activeId
        : null,
    };
  });
}
